import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  School,
  CalendarToday,
  CheckCircle,
  ContactPhone,
  Cancel,
  Info,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../utils/api';
import PageShell from '../../components/shared/PageShell';
import { RADIUS } from '../../theme/tokens';
import EmptyState from '../../components/shared/EmptyState';
import PillTabs from '../../components/shared/PillTabs';
import BottomSheet from '../../components/shared/BottomSheet';

const MotionCard = motion(Card);

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'interested', label: 'Interested' },
  { key: 'enrolled', label: 'Enrolled' },
];

function getStatusColor(status) {
  const map = {
    new: 'info',
    contacted: 'warning',
    interested: 'primary',
    not_interested: 'default',
    enrolled: 'success',
    cancelled: 'error',
  };
  return map[status] || 'default';
}

function getStatusIcon(status) {
  const map = {
    new: <Info />,
    contacted: <ContactPhone />,
    interested: <CheckCircle />,
    enrolled: <CheckCircle />,
    cancelled: <Cancel />,
  };
  return map[status] || <Info />;
}

function StudentInstituteLeads() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [leadToCancel, setLeadToCancel] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    if (activeTab === 'all') {
      setFilteredLeads(leads);
    } else {
      setFilteredLeads(leads.filter(lead => lead.status === activeTab));
    }
  }, [activeTab, leads]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await api.get('/institutes/leads');
      setLeads(response.data.data.leads);
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelLead = async () => {
    if (!leadToCancel) return;
    try {
      await api.put(`/institutes/leads/${leadToCancel.id}/cancel`);
      await fetchLeads();
      setCancelDialogOpen(false);
      setLeadToCancel(null);
    } catch (err) {
      console.error('Error cancelling lead:', err);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  // Lead detail content — shared between mobile BottomSheet and desktop Dialog
  const LeadDetailContent = ({ lead }) => (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box>
        <Typography variant="caption" color="text.secondary">Status</Typography>
        <Box mt={0.5}>
          <Chip
            icon={getStatusIcon(lead.status)}
            label={lead.status.replace('_', ' ')}
            color={getStatusColor(lead.status)}
            sx={{ textTransform: 'capitalize' }}
          />
        </Box>
      </Box>

      {lead.plan && (
        <Box>
          <Typography variant="caption" color="text.secondary">Program</Typography>
          <Typography variant="body1" fontWeight={600}>{lead.plan.name}</Typography>
          <Typography variant="caption" color="text.secondary">{lead.plan.course_type}</Typography>
        </Box>
      )}

      <Box>
        <Typography variant="caption" color="text.secondary">Applied On</Typography>
        <Typography variant="body1">
          {new Date(lead.created_at).toLocaleDateString('en-IN', {
            day: 'numeric', month: 'long', year: 'numeric',
          })}
        </Typography>
      </Box>

      {lead.message && (
        <Box>
          <Typography variant="caption" color="text.secondary">Your Message</Typography>
          <Typography variant="body2" mt={0.5}>{lead.message}</Typography>
        </Box>
      )}

      {lead.preferred_batch_start && (
        <Box>
          <Typography variant="caption" color="text.secondary">Preferred Batch Start</Typography>
          <Typography variant="body2" mt={0.5}>
            {new Date(lead.preferred_batch_start).toLocaleDateString('en-IN')}
          </Typography>
        </Box>
      )}

      {lead.contacted_at && (
        <Box>
          <Typography variant="caption" color="text.secondary">Last Contacted</Typography>
          <Typography variant="body2" mt={0.5}>
            {new Date(lead.contacted_at).toLocaleDateString('en-IN')}
          </Typography>
        </Box>
      )}

      {lead.notes && (
        <Box>
          <Typography variant="caption" color="text.secondary">Institute Notes</Typography>
          <Typography variant="body2" mt={0.5}>{lead.notes}</Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button
          variant="text"
          onClick={() => setDetailOpen(false)}
          sx={{ flex: 1 }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={() => navigate(`/institutes/${lead.institute_id}`)}
          sx={{ flex: 2 }}
        >
          View Institute
        </Button>
      </Box>
    </Box>
  );

  const filterStrip = (
    <Box sx={{ bgcolor: 'background.paper', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', overflowX: 'auto' }}>
      <PillTabs
        tabs={STATUS_TABS}
        value={activeTab}
        onChange={setActiveTab}
      />
    </Box>
  );

  return (
    <PageShell
      title="My institute interests"
      subtitle={`${filteredLeads.length} ${filteredLeads.length === 1 ? 'lead' : 'leads'}`}
      backTo="/student"
      hero={filterStrip}
    >
      <motion.div initial="hidden" animate="visible" variants={containerVariants}>
          {loading ? (
            <>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rectangular" height={140} sx={{ borderRadius: RADIUS.lg, mb: 2 }} />
              ))}
            </>
          ) : filteredLeads.length === 0 ? (
            <EmptyState
              icon={<School />}
              title="No leads yet"
              description="Start exploring institutes and express your interest!"
              actionLabel="Find Institutes"
              onAction={() => navigate('/student/institutes')}
            />
          ) : (
            <AnimatePresence>
              {filteredLeads.map((lead) => (
                <MotionCard
                  key={lead.id}
                  variants={itemVariants}
                  whileTap={{ scale: 0.98 }}
                  sx={{
                    mb: 2,
                    borderRadius: RADIUS.lg,
                    cursor: 'pointer',
                    overflow: 'hidden',
                  }}
                  onClick={() => {
                    setSelectedLead(lead);
                    setDetailOpen(true);
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={1.5}>
                      <Box flex={1}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          {lead.institute?.name || 'Unknown Institute'}
                        </Typography>
                        {lead.plan && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Program: {lead.plan.name}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        icon={getStatusIcon(lead.status)}
                        label={lead.status.replace('_', ' ')}
                        size="small"
                        color={getStatusColor(lead.status)}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </Box>

                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                      <CalendarToday fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        Applied: {new Date(lead.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'short', year: 'numeric',
                        })}
                      </Typography>
                    </Box>

                    {lead.message && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {lead.message}
                      </Typography>
                    )}

                    {lead.status !== 'cancelled' && lead.status !== 'enrolled' && (
                      <Box mt={2}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setLeadToCancel(lead);
                            setCancelDialogOpen(true);
                          }}
                        >
                          Cancel Interest
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </MotionCard>
              ))}
            </AnimatePresence>
          )}
        </motion.div>

      {/* Lead Detail — BottomSheet on mobile, Dialog on desktop */}
      {isMobile ? (
        <BottomSheet
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          title={selectedLead ? (selectedLead.institute?.name || 'Lead Details') : 'Lead Details'}
        >
          {selectedLead && <LeadDetailContent lead={selectedLead} />}
        </BottomSheet>
      ) : (
        <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
          {selectedLead && (
            <>
              <DialogTitle>
                Lead Details
                <Typography variant="caption" display="block" color="text.secondary">
                  {selectedLead.institute?.name}
                </Typography>
              </DialogTitle>
              <DialogContent>
                <LeadDetailContent lead={selectedLead} />
              </DialogContent>
              <DialogActions sx={{ p: 2, pt: 0 }} />
            </>
          )}
        </Dialog>
      )}

      {/* Cancel Confirmation */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Interest?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to cancel your interest in {leadToCancel?.institute?.name}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Keep It</Button>
          <Button onClick={handleCancelLead} color="error" variant="contained">
            Cancel Interest
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}

export default StudentInstituteLeads;
